import MarkdownIt from 'markdown-it'
import markdownItAnchor from 'markdown-it-anchor'
import markdownItFootnote from 'markdown-it-footnote'
import markdownItTaskLists from 'markdown-it-task-lists'
import markdownItTexmath from 'markdown-it-texmath'
import markdownItMultimdTable from 'markdown-it-multimd-table'
import katex from 'katex'
import hljs from 'highlight.js/lib/core'
import bash from 'highlight.js/lib/languages/bash'
import css from 'highlight.js/lib/languages/css'
import dockerfile from 'highlight.js/lib/languages/dockerfile'
import go from 'highlight.js/lib/languages/go'
import javascript from 'highlight.js/lib/languages/javascript'
import json from 'highlight.js/lib/languages/json'
import markdownLanguage from 'highlight.js/lib/languages/markdown'
import python from 'highlight.js/lib/languages/python'
import sql from 'highlight.js/lib/languages/sql'
import typescript from 'highlight.js/lib/languages/typescript'
import xml from 'highlight.js/lib/languages/xml'
import yaml from 'highlight.js/lib/languages/yaml'

hljs.registerLanguage('bash', bash)
hljs.registerLanguage('shell', bash)
hljs.registerLanguage('css', css)
hljs.registerLanguage('dockerfile', dockerfile)
hljs.registerLanguage('go', go)
hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('js', javascript)
hljs.registerLanguage('json', json)
hljs.registerLanguage('markdown', markdownLanguage)
hljs.registerLanguage('md', markdownLanguage)
hljs.registerLanguage('python', python)
hljs.registerLanguage('py', python)
hljs.registerLanguage('sql', sql)
hljs.registerLanguage('typescript', typescript)
hljs.registerLanguage('ts', typescript)
hljs.registerLanguage('html', xml)
hljs.registerLanguage('xml', xml)
hljs.registerLanguage('yaml', yaml)
hljs.registerLanguage('yml', yaml)

const normalizeLanguage = info => String(info || '').trim().split(/\s+/)[0].toLowerCase()

const markdown = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
  typographer: true
})

markdown
  .use(markdownItAnchor, {
    level: [1, 2, 3, 4, 5, 6],
    tabIndex: -1
  })
  .use(markdownItFootnote)
  .use(markdownItTaskLists, {
    enabled: false,
    label: true,
    labelAfter: true
  })
  .use(markdownItTexmath, {
    engine: katex,
    delimiters: ['dollars', 'beg_end'],
    katexOptions: {
      throwOnError: false,
      strict: 'warn',
      trust: false
    }
  })
  .use(markdownItMultimdTable, {
    multiline: true,
    rowspan: true,
    headerless: true
  })

const defaultLinkOpen = markdown.renderer.rules.link_open || function (tokens, index, options, env, renderer) {
  return renderer.renderToken(tokens, index, options)
}

markdown.renderer.rules.link_open = function (tokens, index, options, env, renderer) {
  const href = tokens[index].attrGet('href') || ''
  if (/^https?:\/\//i.test(href)) {
    tokens[index].attrSet('target', '_blank')
    tokens[index].attrSet('rel', 'noopener noreferrer')
  }
  return defaultLinkOpen(tokens, index, options, env, renderer)
}

const defaultImage = markdown.renderer.rules.image
markdown.renderer.rules.image = function (tokens, index, options, env, renderer) {
  tokens[index].attrSet('loading', 'lazy')
  tokens[index].attrSet('decoding', 'async')
  return defaultImage(tokens, index, options, env, renderer)
}

markdown.renderer.rules.table_open = () => (
  '<div class="table-wrapper" tabindex="0" role="region" aria-label="可横向滚动的表格"><table>\n'
)
markdown.renderer.rules.table_close = () => '</table></div>\n'

markdown.renderer.rules.fence = function (tokens, index) {
  const token = tokens[index]
  const language = normalizeLanguage(token.info)
  const escapedSource = markdown.utils.escapeHtml(token.content)

  if (language === 'mermaid') {
    return [
      '<figure class="diagram-block">',
      '<figcaption class="diagram-toolbar">',
      '<span>Mermaid 图表</span>',
      '<span class="diagram-zoom-hint" aria-hidden="true">点击放大</span>',
      '</figcaption>',
      `<pre class="mermaid">${escapedSource}</pre>`,
      '</figure>\n'
    ].join('')
  }

  if (language === 'plantuml' || language === 'puml') {
    return [
      '<figure class="diagram-block plantuml-block">',
      '<figcaption class="diagram-toolbar">',
      '<span>PlantUML 图表</span>',
      '<span class="diagram-zoom-hint" aria-hidden="true">点击放大</span>',
      '</figcaption>',
      '<div class="plantuml-diagram" data-plantuml-diagram>',
      '<p class="plantuml-status" role="status" aria-live="polite">正在准备 PlantUML 图表…</p>',
      '<details class="plantuml-source">',
      '<summary>查看 PlantUML 源码</summary>',
      `<pre><code data-plantuml-source>${escapedSource}</code></pre>`,
      '</details>',
      '</div>',
      '</figure>\n'
    ].join('')
  }

  let highlighted = escapedSource
  if (language && hljs.getLanguage(language)) {
    try {
      highlighted = hljs.highlight(token.content, {
        language,
        ignoreIllegals: true
      }).value
    } catch (error) {
      console.warn(`代码高亮失败: ${language}`, error)
    }
  }

  const safeLanguage = markdown.utils.escapeHtml(language)
  const languageLabel = safeLanguage || 'text'

  return [
    '<figure class="code-block">',
    '<figcaption class="code-toolbar">',
    `<span class="code-language">${languageLabel}</span>`,
    '<button type="button" class="code-copy" data-code-copy aria-label="复制代码">复制</button>',
    '</figcaption>',
    `<pre><code class="hljs${safeLanguage ? ` language-${safeLanguage}` : ''}">${highlighted}</code></pre>`,
    '</figure>\n'
  ].join('')
}

export function renderMarkdown(content) {
  return markdown.render(String(content || '').trim())
}

export function hasMermaidDiagram(content) {
  return /(^|\n)\s*```\s*mermaid(?:\s|\n)/i.test(String(content || ''))
}
